import { defineComponent as _defineComponent } from 'vue';
import { faArrowsAltV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import { Toast } from "@/composables/toast";
import STATES from "@/mvc/dataset/states";
import { useDatatypesMapperStore } from "@/stores/datatypesMapperStore";
import localize from "@/utils/localization";
import DatasetCollectionElementView from "./ListDatasetCollectionElementView.vue";
import CollectionCreator from "@/components/Collections/common/CollectionCreator.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'PairCollectionCreator',
    props: {
        historyId: null,
        initialElements: null,
        defaultHideSourceItems: { type: Boolean },
        fromSelection: { type: Boolean },
        extensions: null
    },
    emits: ["clicked-create", "on-cancel"],
    setup(__props, { emit }) {
        const props = __props;
        const NOT_VALID_ELEMENT_MSG = localize("is not a valid element for this collection");
        const state = ref("build");
        const removeExtensions = ref(true);
        const initialSuggestedName = ref("");
        const invalidElements = ref([]);
        const workingElements = ref([]);
        /** If not `fromSelection`, the manually added elements that will become the pair */
        const inListElements = ref({ forward: undefined, reverse: undefined });
        const allElementsAreInvalid = computed(() => {
            return props.initialElements.length == invalidElements.value.length;
        });
        const noElementsSelected = computed(() => {
            return props.initialElements.length == 0;
        });
        const exactlyTwoValidElements = computed(() => {
            return pairElements.value.forward && pairElements.value.reverse;
        });
        const hideSourceItems = ref(props.defaultHideSourceItems || false);
        const pairElements = computed(() => {
            if (props.fromSelection) {
                return {
                    forward: workingElements.value[0],
                    reverse: workingElements.value[1],
                };
            }
            else {
                return inListElements.value;
            }
        });
        // variables for datatype mapping and then filtering
        const datatypesMapperStore = useDatatypesMapperStore();
        const datatypesMapper = computed(() => datatypesMapperStore.datatypesMapper);
        /** Are we filtering by datatype? */
        const filterExtensions = computed(() => { var _a; return !!datatypesMapper.value && !!((_a = props.extensions) === null || _a === void 0 ? void 0 : _a.length); });
        watch(() => props.initialElements, () => {
            // initialize; and then for any new/removed elements, sync working elements
            _elementsSetUp();
            // TODO: fix this, now either of those will almost always be empty
            if (props.fromSelection && !initialSuggestedName.value) {
                initialSuggestedName.value = _guessNameForPair(workingElements.value[0], workingElements.value[1], removeExtensions.value);
            }
        }, { immediate: true });
        function _elementsSetUp() {
            /** a list of invalid elements and the reasons they aren't valid */
            invalidElements.value = [];
            //TODO: handle fundamental problem of syncing DOM, views, and list here
            /** data for list in progress */
            workingElements.value = [];
            // copy initial list, sort, add ids if needed
            workingElements.value = JSON.parse(JSON.stringify(props.initialElements.slice(0)));
            // reverse the order of the elements to emulate what we have in the history panel
            workingElements.value.reverse();
            // for inListElements, reset their values (in order) to datasets from workingElements
            const inListElementsPrev = inListElements.value;
            inListElements.value = { forward: undefined, reverse: undefined };
            for (const key of ["forward", "reverse"]) {
                const prevElem = inListElementsPrev[key];
                if (!prevElem) {
                    continue;
                }
                const element = workingElements.value.find((e) => { var _a; return e.id === ((_a = inListElementsPrev[key]) === null || _a === void 0 ? void 0 : _a.id); });
                const problem = _isElementInvalid(prevElem);
                if (element) {
                    inListElements.value[key] = element;
                }
                else if (problem) {
                    const invalidMsg = `${prevElem.hid}: ${prevElem.name} ${problem} and ${NOT_VALID_ELEMENT_MSG}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Invalid element"));
                }
                else {
                    const invalidMsg = `${prevElem.hid}: ${prevElem.name} ${localize("has been removed from the collection")}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Invalid element"));
                }
            }
            // TODO: Next thing to add is: If the user adds an uploaded file, that is eventually nuked from workingElements
            //       because it is invalid, Toast an error for that file by keeping track of uploaded ids in a separate list
            _ensureElementIds();
            _validateElements();
        }
        /** add ids to dataset objs in initial list if none */
        function _ensureElementIds() {
            workingElements.value.forEach((element) => {
                if (!Object.prototype.hasOwnProperty.call(element, "id")) {
                    console.warn("Element missing id", element);
                }
            });
            return workingElements.value;
        }
        // /** separate working list into valid and invalid elements for this collection */
        function _validateElements() {
            workingElements.value = workingElements.value.filter((element) => {
                var problem = _isElementInvalid(element);
                if (problem) {
                    invalidElements.value.push(element.name + "  " + problem);
                }
                return !problem;
            });
            return workingElements.value;
        }
        /** describe what is wrong with a particular element if anything */
        function _isElementInvalid(element) {
            var _a;
            if (element.history_content_type === "dataset_collection") {
                return localize("is a collection, this is not allowed");
            }
            var validState = element.state === STATES.OK || STATES.NOT_READY_STATES.includes(element.state);
            if (!validState) {
                return localize("has errored, is paused, or is not accessible");
            }
            if (element.deleted || element.purged) {
                return localize("has been deleted or purged");
            }
            // is the element's extension not a subtype of any of the required extensions?
            if (filterExtensions.value &&
                element.extension &&
                !((_a = datatypesMapper.value) === null || _a === void 0 ? void 0 : _a.isSubTypeOfAny(element.extension, props.extensions))) {
                return localize(`has an invalid extension: ${element.extension}`);
            }
            return null;
        }
        function getPairElement(key) {
            return pairElements.value[key];
        }
        function removeElement(key) {
            pairElements.value[key] = undefined;
        }
        function selectElement(element) {
            var _a, _b;
            if (((_a = pairElements.value.forward) === null || _a === void 0 ? void 0 : _a.id) === element.id) {
                pairElements.value.forward = undefined;
            }
            else if (((_b = pairElements.value.reverse) === null || _b === void 0 ? void 0 : _b.id) === element.id) {
                pairElements.value.reverse = undefined;
            }
            else if (pairElements.value.forward === undefined) {
                pairElements.value.forward = element;
            }
            else if (pairElements.value.reverse === undefined) {
                pairElements.value.reverse = element;
            }
        }
        function swapButton() {
            if (!exactlyTwoValidElements.value) {
                return;
            }
            if (props.fromSelection) {
                workingElements.value = [workingElements.value[1], workingElements.value[0]];
            }
            else {
                inListElements.value = {
                    forward: inListElements.value.reverse,
                    reverse: inListElements.value.forward,
                };
            }
        }
        function addUploadedFiles(files) {
            // Any added files are added to workingElements in _elementsSetUp
            // The user will have to manually select the files to add them to the pair
            // Check for validity of uploads
            files.forEach((file) => {
                const problem = _isElementInvalid(file);
                if (problem) {
                    const invalidMsg = `${file.hid}: ${file.name} ${problem} and ${NOT_VALID_ELEMENT_MSG}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Uploaded item invalid for pair"));
                }
            });
        }
        function clickedCreate(collectionName) {
            if (state.value !== "error" && exactlyTwoValidElements.value) {
                const returnedPair = {
                    forward: pairElements.value.forward,
                    reverse: pairElements.value.reverse,
                    name: collectionName,
                };
                emit("clicked-create", returnedPair, collectionName, hideSourceItems.value);
            }
        }
        function removeExtensionsToggle() {
            removeExtensions.value = !removeExtensions.value;
            initialSuggestedName.value = _guessNameForPair(workingElements.value[0], workingElements.value[1], removeExtensions.value);
        }
        function _guessNameForPair(fwd, rev, removeExtensions) {
            var _a, _b;
            removeExtensions = removeExtensions ? removeExtensions : removeExtensions;
            var fwdName = (_a = fwd.name) !== null && _a !== void 0 ? _a : "";
            var revName = (_b = rev.name) !== null && _b !== void 0 ? _b : "";
            var lcs = _naiveStartingAndEndingLCS(fwdName, revName);
            /** remove url prefix if files were uploaded by url */
            var lastDotIndex = lcs.lastIndexOf(".");
            var lastSlashIndex = lcs.lastIndexOf("/");
            var extension = lcs.slice(lastDotIndex, lcs.length);
            if (lastSlashIndex > 0) {
                var urlprefix = lcs.slice(0, lastSlashIndex + 1);
                lcs = lcs.replace(urlprefix, "");
                fwdName = fwdName.replace(extension, "");
                revName = revName.replace(extension, "");
            }
            if (removeExtensions) {
                if (lastDotIndex > 0) {
                    lcs = lcs.replace(extension, "");
                    fwdName = fwdName.replace(extension, "");
                    revName = revName.replace(extension, "");
                }
            }
            return lcs || `${fwdName} & ${revName}`;
        }
        function onUpdateHideSourceItems(newHideSourceItems) {
            hideSourceItems.value = newHideSourceItems;
        }
        function _naiveStartingAndEndingLCS(s1, s2) {
            var i = 0;
            var j = 0;
            var fwdLCS = "";
            var revLCS = "";
            while (i < s1.length && i < s2.length) {
                if (s1[i] !== s2[i]) {
                    break;
                }
                fwdLCS += s1[i];
                i += 1;
            }
            if (i === s1.length) {
                return s1;
            }
            if (i === s2.length) {
                return s2;
            }
            i = s1.length - 1;
            j = s2.length - 1;
            while (i >= 0 && j >= 0) {
                if (s1[i] !== s2[j]) {
                    break;
                }
                revLCS = [s1[i], revLCS].join("");
                i -= 1;
                j -= 1;
            }
            return fwdLCS + revLCS;
        }
        return { __sfc: true, NOT_VALID_ELEMENT_MSG, props, emit, state, removeExtensions, initialSuggestedName, invalidElements, workingElements, inListElements, allElementsAreInvalid, noElementsSelected, exactlyTwoValidElements, hideSourceItems, pairElements, datatypesMapperStore, datatypesMapper, filterExtensions, _elementsSetUp, _ensureElementIds, _validateElements, _isElementInvalid, getPairElement, removeElement, selectElement, swapButton, addUploadedFiles, clickedCreate, removeExtensionsToggle, _guessNameForPair, onUpdateHideSourceItems, _naiveStartingAndEndingLCS, faArrowsAltV, FontAwesomeIcon, BAlert, BButton, localize, DatasetCollectionElementView, CollectionCreator };
    }
});
